import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Upload, File, Trash2, Loader2, Download, Eye, MessageSquare, History, Package, Search, Tag as TagIcon, FileText, Sparkles } from "lucide-react";
import { AttachmentComments } from "@/components/AttachmentComments";
import { AttachmentVersionHistory } from "@/components/AttachmentVersionHistory";
import { AttachmentComparison } from "@/components/AttachmentComparison";
import { AttachmentTags } from "@/components/AttachmentTags";
import { DocumentSummary } from "@/components/DocumentSummary";
import { ClaimsAssistant } from "@/components/ClaimsAssistant";
import JSZip from "jszip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Attachment {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number;
  created_at: string;
  version: number;
  parent_attachment_id: string | null;
  extracted_text: string | null;
  ocr_processed: boolean;
}

const ClaimAttachments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [showComparisonDialog, setShowComparisonDialog] = useState(false);
  const [compareVersions, setCompareVersions] = useState<{ version1: any; version2: any } | null>(null);
  const [selectedForDownload, setSelectedForDownload] = useState<Set<string>>(new Set());
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [allTags, setAllTags] = useState<string[]>([]);
  const [showTagsDialog, setShowTagsDialog] = useState(false);
  const [ocrProcessing, setOcrProcessing] = useState<Set<string>>(new Set());

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
        if (!session?.user) {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session?.user) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user && id) {
      fetchAttachments();
      fetchAllTags();
    }
  }, [user, id]);

  // Real-time subscription for new attachments
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel('claim-attachments-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'claim_attachments',
          filter: `claim_id=eq.${id}`
        },
        (payload) => {
          console.log('New attachment uploaded:', payload);
          
          // Add the new attachment to the beginning of the list
          setAttachments((prev) => [payload.new as Attachment, ...prev]);
          
          // Show toast notification
          toast.info(`New document uploaded: ${payload.new.file_name}`);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id]);

  const fetchAttachments = async () => {
    try {
      const { data, error } = await supabase
        .from("claim_attachments")
        .select("id, file_name, file_path, file_type, file_size, created_at, version, parent_attachment_id, extracted_text, ocr_processed")
        .eq("claim_id", id)
        .is("parent_attachment_id", null) // Only show original files, not versions
        .order("created_at", { ascending: false });

      if (error) throw error;

      setAttachments(data || []);
    } catch (error: any) {
      toast.error("Failed to load attachments");
      console.error("Error fetching attachments:", error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const fetchAllTags = async () => {
    if (!id) return;
    
    try {
      // Get all attachment IDs for this claim
      const { data: claimAttachments, error: attachError } = await supabase
        .from("claim_attachments")
        .select("id")
        .eq("claim_id", id);

      if (attachError) throw attachError;
      
      if (!claimAttachments || claimAttachments.length === 0) {
        setAllTags([]);
        return;
      }

      const attachmentIds = claimAttachments.map(a => a.id);

      // Get all tags for these attachments
      const { data, error } = await supabase
        .from("attachment_tags")
        .select("tag")
        .in("attachment_id", attachmentIds);

      if (error) throw error;

      const uniqueTags = [...new Set(data?.map(t => t.tag) || [])];
      setAllTags(uniqueTags);
    } catch (error: any) {
      console.error("Error fetching tags:", error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSize = 20 * 1024 * 1024; // 20MB

      if (file.size > maxSize) {
        toast.error("File size must be less than 20MB");
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !user || !id) return;

    setUploading(true);
    try {
      // Upload file to storage
      const fileExt = selectedFile.name.split(".").pop();
      const fileName = `${id}/${Date.now()}.${fileExt}`;
      const { error: uploadError } = await supabase.storage
        .from("claim-attachments")
        .upload(fileName, selectedFile);

      if (uploadError) throw uploadError;

      // Create attachment record
      const { error: dbError } = await supabase.from("claim_attachments").insert({
        claim_id: id,
        file_name: selectedFile.name,
        file_path: fileName,
        file_type: selectedFile.type,
        file_size: selectedFile.size,
        uploaded_by: user.id,
      });

      if (dbError) throw dbError;

      // Send document upload email notification
      try {
        await supabase.functions.invoke("send-document-upload-email", {
          body: {
            claimId: id,
            fileName: selectedFile.name,
            uploadedBy: user.id,
          },
        });
      } catch (emailError) {
        console.error("Failed to send email notification:", emailError);
        // Don't fail the upload if email fails
      }

      toast.success("File uploaded successfully");
      setSelectedFile(null);
      // Reset file input
      const fileInput = document.getElementById("file-upload") as HTMLInputElement;
      if (fileInput) fileInput.value = "";
      fetchAttachments();
    } catch (error: any) {
      toast.error(error.message || "Failed to upload file");
      console.error("Error uploading file:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const { data, error } = await supabase.storage
        .from("claim-attachments")
        .download(attachment.file_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error("Failed to download file");
      console.error("Error downloading file:", error);
    }
  };

  const handlePreview = async (attachment: Attachment) => {
    const fileType = attachment.file_type.toLowerCase();
    
    // Check if file type is previewable
    if (!fileType.includes('image') && !fileType.includes('pdf')) {
      toast.error("Preview not available for this file type");
      return;
    }

    setLoadingPreview(true);
    setPreviewAttachment(attachment);

    try {
      const { data, error } = await supabase.storage
        .from("claim-attachments")
        .download(attachment.file_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      setPreviewUrl(url);
    } catch (error: any) {
      toast.error("Failed to load preview");
      console.error("Error loading preview:", error);
      setPreviewAttachment(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setPreviewAttachment(null);
  };

  const handleDelete = async (attachment: Attachment) => {
    if (!confirm("Are you sure you want to delete this file?")) return;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("claim-attachments")
        .remove([attachment.file_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from("claim_attachments")
        .delete()
        .eq("id", attachment.id);

      if (dbError) throw dbError;

      toast.success("File deleted successfully");
      setAttachments((prev) => prev.filter((a) => a.id !== attachment.id));
    } catch (error: any) {
      toast.error("Failed to delete file");
      console.error("Error deleting file:", error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const handleCompareVersions = (version1: any, version2: any) => {
    setCompareVersions({ version1, version2 });
    setShowVersionDialog(false);
    setShowComparisonDialog(true);
  };

  const handleToggleSelection = (attachmentId: string) => {
    setSelectedForDownload(prev => {
      const newSet = new Set(prev);
      if (newSet.has(attachmentId)) {
        newSet.delete(attachmentId);
      } else {
        newSet.add(attachmentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedForDownload.size === attachments.length) {
      setSelectedForDownload(new Set());
    } else {
      setSelectedForDownload(new Set(attachments.map(a => a.id)));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedForDownload.size === 0) {
      toast.error("Please select files to download");
      return;
    }

    setBulkDownloading(true);
    try {
      const zip = new JSZip();
      const selectedAttachments = attachments.filter(a => selectedForDownload.has(a.id));

      // Download all files and add to zip
      await Promise.all(
        selectedAttachments.map(async (attachment) => {
          const { data, error } = await supabase.storage
            .from("claim-attachments")
            .download(attachment.file_path);

          if (error) throw error;
          zip.file(attachment.file_name, data);
        })
      );

      // Generate and download zip
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const a = document.createElement("a");
      a.href = url;
      a.download = `claim-${id}-attachments.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Downloaded ${selectedForDownload.size} file(s)`);
      setSelectedForDownload(new Set());
    } catch (error: any) {
      toast.error("Failed to download files");
      console.error("Error downloading files:", error);
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleExtractText = async (attachment: Attachment) => {
    if (ocrProcessing.has(attachment.id)) return;

    setOcrProcessing(prev => new Set(prev).add(attachment.id));
    toast.info("Starting text extraction...");

    try {
      // Get public URL for the file
      const { data: urlData } = await supabase.storage
        .from("claim-attachments")
        .createSignedUrl(attachment.file_path, 3600); // 1 hour expiry

      if (!urlData?.signedUrl) {
        throw new Error("Failed to generate file URL");
      }

      const { data, error } = await supabase.functions.invoke("extract-text-ocr", {
        body: {
          attachmentId: attachment.id,
          fileUrl: urlData.signedUrl,
          fileType: attachment.file_type
        }
      });

      if (error) throw error;

      if (data.error) {
        throw new Error(data.error);
      }

      toast.success(`Extracted ${data.characterCount} characters`);
      fetchAttachments(); // Refresh to show updated OCR status
    } catch (error: any) {
      console.error("Error extracting text:", error);
      toast.error(error.message || "Failed to extract text");
    } finally {
      setOcrProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  const filteredAttachments = attachments.filter(attachment => {
    // Search filter
    const matchesSearch = searchQuery === "" || 
      attachment.file_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (attachment.extracted_text && attachment.extracted_text.toLowerCase().includes(searchQuery.toLowerCase()));

    // Tag filter (implement when tags are loaded per attachment)
    const matchesTag = !selectedTag; // For now, show all if no tag selected

    return matchesSearch && matchesTag;
  });

  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />

      <main className="flex-1 container mx-auto px-4 py-8 mt-20">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="outline" onClick={() => navigate(`/claim/${id}`)}>
              Back to Claim
            </Button>
            <div>
              <h1 className="text-4xl font-bold">Claim Attachments</h1>
              <p className="text-muted-foreground">Upload and manage documents for this claim</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Search and Filter Bar */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search files and extracted text..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {allTags.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => setSelectedTag(null)}
                    >
                      <TagIcon className="h-4 w-4 mr-2" />
                      All Tags
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Upload Document</CardTitle>
                <CardDescription>
                  Upload supporting documents for your claim (Max 20MB per file)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUpload} className="space-y-4">
                  <div>
                    <Label htmlFor="file-upload">Select File</Label>
                    <Input
                      id="file-upload"
                      type="file"
                      onChange={handleFileChange}
                      className="mt-1"
                      required
                    />
                    {selectedFile && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                      </p>
                    )}
                  </div>
                  <Button type="submit" disabled={uploading || !selectedFile}>
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Upload File
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Uploaded Documents</CardTitle>
                    <CardDescription>
                      {filteredAttachments.length} of {attachments.length} document{attachments.length !== 1 ? "s" : ""}
                      {searchQuery && " (filtered)"}
                      {selectedForDownload.size > 0 && (
                        <span className="ml-2">• {selectedForDownload.size} selected</span>
                      )}
                    </CardDescription>
                  </div>
                  {attachments.length > 0 && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        {selectedForDownload.size === attachments.length ? "Deselect All" : "Select All"}
                      </Button>
                      {selectedForDownload.size > 0 && (
                        <Button
                          size="sm"
                          onClick={handleBulkDownload}
                          disabled={bulkDownloading}
                        >
                          {bulkDownloading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Package className="mr-2 h-4 w-4" />
                              Download Selected
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loadingAttachments ? (
                  <div className="text-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                  </div>
                ) : attachments.length === 0 ? (
                  <div className="text-center py-8">
                    <File className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No documents uploaded yet</p>
                  </div>
                ) : filteredAttachments.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No documents match your search</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-12">
                            <input
                              type="checkbox"
                              checked={selectedForDownload.size === attachments.length}
                              onChange={handleSelectAll}
                              className="cursor-pointer"
                            />
                          </TableHead>
                          <TableHead>File Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Size</TableHead>
                          <TableHead>Uploaded</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAttachments.map((attachment) => (
                          <TableRow key={attachment.id}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedForDownload.has(attachment.id)}
                                onChange={() => handleToggleSelection(attachment.id)}
                                className="cursor-pointer"
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                <File className="h-4 w-4 text-muted-foreground" />
                                {attachment.file_name}
                              </div>
                            </TableCell>
                            <TableCell>{attachment.file_type || "Unknown"}</TableCell>
                            <TableCell>{formatFileSize(attachment.file_size)}</TableCell>
                            <TableCell>
                              {new Date(attachment.created_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {(attachment.file_type.toLowerCase().includes('image') || 
                                  attachment.file_type.toLowerCase().includes('pdf')) && (
                                  <>
                                    {!attachment.ocr_processed ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleExtractText(attachment)}
                                        disabled={ocrProcessing.has(attachment.id)}
                                        title="Extract Text (OCR)"
                                      >
                                        {ocrProcessing.has(attachment.id) ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <FileText className="h-4 w-4" />
                                        )}
                                      </Button>
                                    ) : (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled
                                        title="Text extracted"
                                      >
                                        <FileText className="h-4 w-4 text-green-500" />
                                      </Button>
                                    )}
                                    <DocumentSummary
                                      attachmentId={attachment.id}
                                      claimId={id!}
                                      fileName={attachment.file_name}
                                      fileType={attachment.file_type}
                                      extractedText={attachment.extracted_text}
                                      onGetSignedUrl={async () => {
                                        const { data } = await supabase.storage
                                          .from("claim-attachments")
                                          .createSignedUrl(attachment.file_path, 3600);
                                        return data?.signedUrl || null;
                                      }}
                                    />
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handlePreview(attachment)}
                                      title="Preview"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedAttachment(attachment);
                                    setShowTagsDialog(true);
                                  }}
                                  title="Tags"
                                >
                                  <TagIcon className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedAttachment(attachment);
                                    setShowCommentsDialog(true);
                                  }}
                                  title="Comments"
                                >
                                  <MessageSquare className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedAttachment(attachment);
                                    setShowVersionDialog(true);
                                  }}
                                  title="Version History"
                                >
                                  <History className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownload(attachment)}
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(attachment)}
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />

      {/* Preview Dialog */}
      <Dialog open={!!previewAttachment} onOpenChange={handleClosePreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{previewAttachment?.file_name}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(90vh-100px)]">
            {loadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : previewUrl && previewAttachment ? (
              previewAttachment.file_type.toLowerCase().includes('image') ? (
                <img 
                  src={previewUrl} 
                  alt={previewAttachment.file_name}
                  className="w-full h-auto rounded-lg"
                />
              ) : previewAttachment.file_type.toLowerCase().includes('pdf') ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-[70vh] rounded-lg border"
                  title={previewAttachment.file_name}
                />
              ) : null
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Comments Dialog */}
      <Dialog open={showCommentsDialog} onOpenChange={setShowCommentsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Comments: {selectedAttachment?.file_name}</DialogTitle>
          </DialogHeader>
          {selectedAttachment && user && (
            <AttachmentComments attachmentId={selectedAttachment.id} user={user} />
          )}
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showVersionDialog} onOpenChange={setShowVersionDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Version History: {selectedAttachment?.file_name}</DialogTitle>
          </DialogHeader>
          {selectedAttachment && user && id && (
            <AttachmentVersionHistory
              attachmentId={selectedAttachment.id}
              claimId={id}
              currentVersion={selectedAttachment.version}
              fileName={selectedAttachment.file_name}
              user={user}
              onNewVersion={fetchAttachments}
              onCompare={handleCompareVersions}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Version Comparison Dialog */}
      <Dialog open={showComparisonDialog} onOpenChange={setShowComparisonDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Compare Versions: {selectedAttachment?.file_name}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[calc(90vh-100px)]">
            {compareVersions && (
              <AttachmentComparison
                version1={compareVersions.version1}
                version2={compareVersions.version2}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tags Dialog */}
      <Dialog open={showTagsDialog} onOpenChange={setShowTagsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Tags: {selectedAttachment?.file_name}</DialogTitle>
          </DialogHeader>
          {selectedAttachment && user && (
            <AttachmentTags attachmentId={selectedAttachment.id} user={user} />
          )}
        </DialogContent>
      </Dialog>

      {/* Claims Assistant Chatbot */}
      <ClaimsAssistant claimId={id} />
    </div>
  );
};

export default ClaimAttachments;
